import { useState, useEffect, useContext } from 'react';
import {
    buttonLoadingTitles,
    pageNames,
    physicalInspectionCriteria,
    physicalInspectionStatuses,
    releaseLineStatuses,
    stepConfirmationStatuses,
} from './utils/data';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import {
    getDefaultInspectionRequirements,
    getInvPhotos,
    getRoChatterMentionIds,
    sendChatterMessage,
    submitTracePhoto,
    updateStoredHeroPhotoNames,
    updateHeroPhotos,
    updateReleaseAndOrReleaseLineStatus,
    updateReleaseInspectionStatus,
    savePhysicalCheckboxes,
} from './utils/guided-interface-functions';
import { base64ToBlob } from '../../../utils/commonAPICalls';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { GuidedInterfaceContext } from './context/GuidedInterfaceContext.js';
import PhysicalCamera from './PhysicalCamera';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Modal from '../../../components/Modal.js';
import _ from 'lodash';
import logError from '../../../utils/errorUtil.js';

export default function Physical({
    currentReleaseLine,
    currentUser,
    handlePageChange,
    releaseName,
    returnToReleaseLines,
    releaseId,
}) {
    const inspectionRequirementsAllowedForPartialPass = [physicalInspectionCriteria.PART_NUMBER];
    const [submissionStatus, setSubmissionStatus] = useState({
        isSubmitting: false,
        photos: '',
        heroImages: '',
        chatter: '',
        inspectionStatus: '',
        checkboxes: '',
        retry: 0,
        type: '',
    });
    const [loading, setLoading] = useState({
        photos: true,
        camera: true,
    });
    const [buttonLoading, setButtonLoading] = useState(null);
    const [customExpirationDate, setCustomExpirationDate] = useState('');
    const [customPartNumber, setCustomPartNumber] = useState('');
    const [customSerialNumber, setCustomSerialNumber] = useState('');
    const [expirationRejectionModal, setExpirationRejectionModal] = useState(false);
    const [partNumberRejectionModal, setPartNumberRejectionModal] = useState(false);
    const [serialNumberRejectionModal, setSerialNumberRejectionModal] = useState(false);
    const [quantityMismatchModal, setQuantityMismatchModal] = useState(false);

    // Verify quantity input state — restore entered quantity from saved physicalCheckboxes if available,
    // This logic ultimately defaults to zero if no stored quantity is found
    const [verifyQuantity, setVerifyQuantity] = useState(() => {
        const saved = currentReleaseLine.physicalCheckboxes;
        if (Array.isArray(saved)) {
            const qtyReq = saved.find((req) => req.id === physicalInspectionCriteria.QUANTITY);
            if (qtyReq?.value != null && String(qtyReq.value) !== '') return Math.abs(Number(String(qtyReq.value)));
        }
        return String('');
    });

    // Camera State
    const [showCamera, setShowCamera] = useState(false);
    const [videoStream, setVideoStream] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
    const [photos, setPhotos] = useState({ toAdd: [], current: [], added: [] });
    const [storedHeroPhotoNames, setStoredHeroPhotoNames] = useState([]);

    // We needed to do a remap for the quantity value because all quantity is now pass = true
    // TODO: Remove map after 9/1/26
    const [inspectionRequirements, setInspectionRequirements] = useState(() => {
        const initial = currentReleaseLine.physicalCheckboxes ?? getDefaultInspectionRequirements(currentReleaseLine);
        return initial.map((req) => (req.id === physicalInspectionCriteria.QUANTITY ? { ...req, pass: true } : req));
    });

    const { partInspectionSeconds, partInspectionTimeDisplay, setShowNotification, startTimer, stopTimer } =
        useContext(GuidedInterfaceContext);

    const findAddedHeroPhotos = (photos) => {
        return photos.filter((photo) => photo.isHero && !storedHeroPhotoNames.includes(photo.name));
    };

    const resetPhysicalInspectionVariables = () => {
        setInspectionRequirements(getDefaultInspectionRequirements(currentReleaseLine));
        setVerifyQuantity('');
        setCustomExpirationDate('');
        setCustomPartNumber('');
        setCustomSerialNumber('');
    };

    const handlePhotoSubmissions = async (isAccepted) => {
        try {
            await handleUpdateStoredHeroPhotoNames();
            if (photos.added.length) {
                await handlePhotoUpload(photos.added, isAccepted); //? ALSO UPDATES HERO STATUSES
            }

            const photosChanged = findAddedHeroPhotos(photos.current);
            if (photosChanged.length) {
                await handleHeroPhotoUpdate(photosChanged, isAccepted);
            }
        } catch (error) {
            // Escape to log but also skip updating hero photo names
            logError(error, 'handlePhotoSubmissions');
        }
    };

    const handleUpdateStoredHeroPhotoNames = async () => {
        const allPhotos = photos.added.concat(photos.current);
        const heroPhotoNames = allPhotos
            .filter((photo) => photo.isHero)
            .map((photo) => photo.name)
            .filter((photoName) => photoName !== undefined);
        await updateStoredHeroPhotoNames(currentReleaseLine.invLineName, _.uniq(heroPhotoNames), currentUser);
    };

    const handleSaveStatusAndTime = async (status) => {
        setSubmissionStatus((prev) => ({ ...prev, inspectionStatus: stepConfirmationStatuses.PENDING }));

        const response = await updateReleaseInspectionStatus(
            status,
            'physical',
            currentReleaseLine.releaseLineName,
            releaseName,
            partInspectionSeconds,
            currentUser
        );
        if (!response) {
            setSubmissionStatus((prev) => ({
                ...prev,
                inspectionStatus: stepConfirmationStatuses.FAIL,
                retry: prev.retry + 1,
            }));
        } else {
            setSubmissionStatus((prev) => ({ ...prev, inspectionStatus: stepConfirmationStatuses.SUCCESS }));
        }
    };

    const handlePhotoUpload = async (photoData) => {
        setSubmissionStatus((prev) => ({ ...prev, photos: stepConfirmationStatuses.PENDING }));
        const responses = [];
        for (const photo of photoData) {
            const blob = base64ToBlob(photo.url.replace('data:image/png;base64,', ''), 'image/png');
            const isHero = photo.isHero;
            const authData = {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            };
            const response = await submitTracePhoto(blob, isHero, authData, currentReleaseLine, releaseName);

            responses.push({
                ...photo,
                url: photo.url,
                uploaded: response.success ? 1 : 0,
            });
        }
        const failedUploads = responses.filter((photo) => photo.uploaded === 0);

        if (failedUploads.length) {
            setSubmissionStatus((prev) => ({
                ...prev,
                photos: stepConfirmationStatuses.FAIL,
                retry: prev.retry + 1,
            }));
            throw Error('Failure in handlePhotoUpload');
        } else setSubmissionStatus((prev) => ({ ...prev, photos: stepConfirmationStatuses.SUCCESS }));
        setPhotos({
            ...photos,
            toAdd: [],
            added: [...failedUploads],
        });
    };

    const handleHeroPhotoUpdate = async (photosChanged) => {
        setSubmissionStatus((prev) => ({ ...prev, heroImages: stepConfirmationStatuses.PENDING }));
        const response = await updateHeroPhotos(photosChanged, currentUser, {
            ...currentReleaseLine,
            releaseId,
            releaseName,
        });
        if (!response) {
            setSubmissionStatus((prev) => ({
                ...prev,
                heroImages: stepConfirmationStatuses.FAIL,
                retry: prev.retry + 1,
            }));
            throw Error('Failure in handleHeroPhotoUpdate');
        } else {
            setSubmissionStatus((prev) => ({ ...prev, heroImages: stepConfirmationStatuses.SUCCESS }));
        }
    };

    const handleChatterUpload = async (inspectionPassed = false, functionForAdditionalMentionIds = () => []) => {
        setSubmissionStatus((prev) => ({ ...prev, chatter: stepConfirmationStatuses.PENDING }));

        const chatterMessage = getChatterMessage(inspectionPassed);
        if (!chatterMessage) return;

        const response = await sendChatterMessage(
            currentUser,
            currentReleaseLine,
            chatterMessage,
            functionForAdditionalMentionIds()
        );

        if (!response.success) {
            setSubmissionStatus((prev) => ({
                ...prev,
                chatter: stepConfirmationStatuses.FAIL,
                retry: prev.retry + 1,
            }));
        } else {
            setSubmissionStatus((prev) => ({ ...prev, chatter: stepConfirmationStatuses.SUCCESS }));
        }
    };

    const getChatterMessage = (inspectionPassed) => {
        let reviewRequestMessage = '';
        if (inspectionPassed) {
            reviewRequestMessage = 'Passed inspection criteria: \n';
        } else {
            reviewRequestMessage = 'Did not pass inspection criteria: \n';
        }

        // For each inspection requirement that did not pass, add it to the string to insert into the chatter message
        inspectionRequirements.forEach((req) => {
            // Handle quantity logic regardless of pass or failure
            // TODO: Remove after 9/1/26 (copy of message near inspectionRequirement state logic above)
            const quantityMismatch = Math.abs(Number(verifyQuantity)) !== Math.abs(Number(currentReleaseLine.quantity));
            if (req.id === physicalInspectionCriteria.QUANTITY && quantityMismatch) {
                reviewRequestMessage += `\n- Quantity mismatch - Inspector entered ${verifyQuantity} but expected ${Math.abs(Number(currentReleaseLine.quantity))}`;
                return;
            }

            if (!req.pass) {
                if (
                    req.id === physicalInspectionCriteria.EXPIRATION_DATE ||
                    req.id === physicalInspectionCriteria.SPECIAL_KEYWORD
                ) {
                    if (reviewRequestMessage.toLowerCase().includes('verify part expiration')) return;
                    // Write the short expiration message manually so we don't write the other html message for specialKeyword
                    reviewRequestMessage += `\n- Verify part expiration`;
                    // Write custom message if the user did not select a date
                    const customDateMessage =
                        customExpirationDate === physicalInspectionCriteria.NO_SHELF_LIFE
                            ? ` - Inspector marked as No Shelf Life`
                            : ` - Inspector manually entered date: ${customExpirationDate}`;
                    reviewRequestMessage += customDateMessage;
                } else if (req.id === physicalInspectionCriteria.PART_NUMBER) {
                    reviewRequestMessage += `\n- ${req.requirement}`;
                    reviewRequestMessage += ` - Inspector manually entered part number: ${customPartNumber}`;
                } else if (req.id === physicalInspectionCriteria.SERIAL_NUMBER) {
                    reviewRequestMessage += `\n- ${req.requirement}`;
                    reviewRequestMessage += ` - Inspector manually entered serial number: ${customSerialNumber}`;
                } else {
                    reviewRequestMessage += `\n- ${req.requirement}`;
                }
            }
        });

        return reviewRequestMessage;
    };

    const navigateBack = () => {
        !showCamera ? handlePageChange(pageNames.RELEASE_LINES) : setShowCamera(false);
    };

    const updateReleaseStatus = async (approved) => {
        let updatedReleaseLineStatus;

        if (approved) updatedReleaseLineStatus = releaseLineStatuses.PICKED;
        if (!approved) updatedReleaseLineStatus = releaseLineStatuses.PENDING_SALES;

        await updateReleaseAndOrReleaseLineStatus(
            currentUser,
            releaseId,
            currentReleaseLine.releaseLineId,
            updatedReleaseLineStatus
        );
    };

    const submitPhotoApproval = async (destination) => {
        // Set the accepted button to start loading spinner
        setButtonLoading(
            destination === pageNames.TRACE
                ? buttonLoadingTitles.PHOTOS_ACCEPTED_TRACE
                : buttonLoadingTitles.PHOTOS_ACCEPTED_RETURN
        );

        await Promise.all([
            handlePhotoSubmissions(true), //photo uploads and hero image updates
            handleSaveStatusAndTime('complete'), // status and time updates
        ]);

        await updateReleaseStatus(true);

        setSubmissionStatus((prev) => ({
            ...prev,
            isSubmitting: true,
            type: 'complete',
        }));
        handlePageChange(destination, currentReleaseLine.releaseLineId);
    };

    /**
     * Submission logic for the inspection checkbox review page (green and red buttons)
     * @param {string} reviewStatus The status of the review
     * @param {string} customVariable The custom variable to be used for the review
     * @param {string} customValue The custom value to be used for the review
     * @returns {Promise<void>} A promise that resolves when the submission is complete
     */
    const submitInspectionCheckboxReview = async (reviewStatus, customVariable, customValue) => {
        const reviewPassed =
            reviewStatus === physicalInspectionStatuses.INSPECTION_PASSED ||
            reviewStatus === physicalInspectionStatuses.INSPECTION_PARTIAL_PASSED;

        setSubmissionStatus((prev) => ({
            ...prev,
            type: reviewStatus,
        }));

        if (reviewPassed) {
            const inspectionPassCompleted = await handleInspectionPass(reviewStatus);
            if (!inspectionPassCompleted) return;
        }

        if (!reviewPassed) {
            const inspectionRejectCompleted = await handleInspectionReject(customVariable, customValue);
            if (!inspectionRejectCompleted) return;
        }

        let failure = false;

        // Check for failures within the submission steps loading popup modal
        for (const key in submissionStatus) {
            if (submissionStatus[key] === stepConfirmationStatuses.FAIL) failure = true;
        }

        if (submissionStatus.type === 'rejected' && submissionStatus.isSubmitting) return;

        if (failure) {
            if (submissionStatus.retry < 10) {
                setShowNotification({
                    shown: true,
                    status: 'error',
                    title: 'Submission Failed',
                    message: 'Please retry submission',
                });
            } else {
                setShowNotification({
                    shown: true,
                    status: 'error',
                    title: 'Submission Failed',
                    message: 'No retry attempts remaining, returning to previous page in 5 seconds',
                });
                returnToReleaseLines();
                resetPhysicalInspectionVariables();
            }
            return;
        }

        setSubmissionStatus({
            isSubmitting: false,
            photos: '',
            heroImages: '',
            chatter: '',
            inspectionStatus: '',
            checkboxes: '',
            retry: 0,
            type: '',
        });
        setShowCamera(reviewPassed);
        resetPhysicalInspectionVariables();
    };

    /**
     * Branch of logic from within `submitInspectionCheckboxReview` on a rejection
     * @param {string} customVariable Allows for custom data to be set depending on what step a user might be on
     * @param {string} customValue Custom value for the custom variable set above
     * @returns {Promise<boolean>} Whether the rejection submission completed
     */
    const handleInspectionReject = async (customVariable, customValue) => {
        const inputModalsCompleted = await handleInputModalsForUncheckedRequirements(customVariable, customValue);
        if (!inputModalsCompleted) return false;

        setSubmissionStatus((prev) => ({
            ...prev,
            isSubmitting: true,
        }));

        // Set the rejected button to start loading spinner
        setButtonLoading(buttonLoadingTitles.INSPECTION_REJECTED);

        const passedReview = false;
        await Promise.all([
            savePhysicalCheckboxes(currentUser, currentReleaseLine.releaseLineName, releaseName, inspectionRequirements),
            handleChatterUpload(),
            handleSaveStatusAndTime('rejected'),
            updateReleaseStatus(passedReview),
        ]);

        setButtonLoading(null);
        setSubmissionStatus((prev) => ({
            ...prev,
            isSubmitting: false,
            type: physicalInspectionStatuses.INSPECTION_REJECTED,
        }));

        returnToReleaseLines();
        return true;
    };

    /**
     * Branch of logic from within `submitInspectionCheckboxReview` on a passed inspection
     * @param {string} reviewStatus The status of the review
     * @returns {Promise<void>} A promise that resolves when the submission is complete
     */
    const handleInspectionPass = async (reviewStatus) => {
        const partialPass = reviewStatus === physicalInspectionStatuses.INSPECTION_PARTIAL_PASSED;
        setSubmissionStatus((prev) => ({
            ...prev,
            isSubmitting: false,
            checkboxes: stepConfirmationStatuses.PENDING,
            chatter: partialPass ? stepConfirmationStatuses.PENDING : '',
            inspectionStatus: stepConfirmationStatuses.PENDING,
        }));

        const inputModalsCompleted = await handleInputModalsForUncheckedRequirements();
        if (!inputModalsCompleted) return false;

        setButtonLoading(buttonLoadingTitles.INSPECTION_PASSED);
        setSubmissionStatus((prev) => ({
            ...prev,
            isSubmitting: true,
        }));

        let updatedInspectionRequirements = inspectionRequirements;

        // Ensure that even with a partial pass the inspection requirements are saved as passed
        // Send relevant chatter message
        if (partialPass) {
            if (customPartNumber) {
                updatedInspectionRequirements = inspectionRequirements.map((req) =>
                    req.id === physicalInspectionCriteria.PART_NUMBER ? { ...req, value: customPartNumber, pass: true } : req
                );
                setInspectionRequirements(updatedInspectionRequirements);
            }

            const inspectionPassed = true;
            await handleChatterUpload(inspectionPassed, () => getRoChatterMentionIds(currentReleaseLine));
        }

        await savePhysicalCheckboxes(currentUser, currentReleaseLine.releaseLineName, releaseName, updatedInspectionRequirements);

        setButtonLoading(null);
        return true;
    };

    const handleInputModalsForUncheckedRequirements = async (customVariable, customValue) => {
        const expirationData = inspectionRequirements.find((req) => req.id === physicalInspectionCriteria.EXPIRATION_DATE);
        const specialKeywordData = inspectionRequirements.find(
            (req) => req.id === physicalInspectionCriteria.SPECIAL_KEYWORD
        );
        const partNumberData = inspectionRequirements.find((req) => req.id === physicalInspectionCriteria.PART_NUMBER);
        const serialNumberData = inspectionRequirements.find((req) => req.id === physicalInspectionCriteria.SERIAL_NUMBER);

        // Check if either of the expiration fields are not a pass if they're required
        const expirationAndKeywordFail = expirationData?.pass === false || specialKeywordData?.pass === false;
        // Check that one expiration failed, the user didn't enter a custom date, and they didn't click confirm for no date required
        // This is basically checking to see if this is the first time the expiration modal has popped up
        if (
            expirationAndKeywordFail &&
            !customExpirationDate &&
            !(customVariable === physicalInspectionCriteria.NO_SHELF_LIFE && customValue === true)
        ) {
            setExpirationRejectionModal(true);
            return false;
        }

        if (!partNumberData?.pass && !customPartNumber) {
            setPartNumberRejectionModal(true);
            return false;
        }

        if (!serialNumberData?.pass && !customSerialNumber) {
            setSerialNumberRejectionModal(true);
            return false;
        }

        if (Math.abs(Number(verifyQuantity)) !== Math.abs(Number(currentReleaseLine.quantity)) && !quantityMismatchModal) {
            if (verifyQuantity === '') setVerifyQuantity('0');
            setQuantityMismatchModal(true);
            return false;
        }

        return true;
    };

    useEffect(() => {
        const timer = startTimer();
        const { invLineName, tracePicturesLink } = currentReleaseLine;

        getInvPhotos(invLineName, tracePicturesLink, currentUser).then((dropboxPhotos) => {
            const previousHeroPhotos = dropboxPhotos.heroPhotoNames;
            setStoredHeroPhotoNames(previousHeroPhotos ?? []);
            setPhotos((prev) => ({
                ...prev,
                current: dropboxPhotos?.photos.map((photo) => ({
                    url: `data:image/png;base64, ${photo.data}`,
                    name: photo.name,
                    // If no previous hero photos mark all as hero
                    isHero: previousHeroPhotos ? dropboxPhotos.heroPhotoNames.includes(photo.name) : true,
                })),
            }));
            setLoading((prev) => ({ ...prev, photos: false }));
        });

        return () => stopTimer(timer);
    }, []);

    // Retry submission - each fail increments the retry + 1, retry button increments by 10 and triggers retry
    useEffect(() => {
        if (submissionStatus.retry > 10) {
            const isAccepted = submissionStatus.type === 'complete';
            if (submissionStatus?.photos === stepConfirmationStatuses.FAIL) {
                handlePhotoUpload(photos.added, isAccepted);
            }
            if (submissionStatus.heroImages === stepConfirmationStatuses.FAIL) {
                const heroPhotosAdded = findAddedHeroPhotos(photos.current);
                updateHeroPhotos(heroPhotosAdded, currentUser, { ...currentReleaseLine, releaseId, releaseName });
            }
            if (submissionStatus.chatter === stepConfirmationStatuses.FAIL) {
                sendChatterMessage(
                    currentUser,
                    currentReleaseLine,
                    getChatterMessage(),
                    getRoChatterMentionIds(currentReleaseLine)
                );
            }
            if (submissionStatus.inspectionStatus === stepConfirmationStatuses.FAIL) {
                handleSaveStatusAndTime(isAccepted ? 'complete' : 'rejected');
            }
        }
    }, [submissionStatus.retry]);

    return (
        <div className='flex flex-col items-center px-8'>
            <header className='flex mt-10 w-full h-10'>
                <button
                    className='flex z-10 justify-center items-center px-3 my-auto w-24 h-10 text-lg text-white rounded-md bg-blue-primary hover:bg-blue-secondary'
                    onClick={navigateBack}
                >
                    <ArrowLeftIcon className='mr-2 w-6 h-6' />
                    <span>Back</span>
                </button>
                <div className='flex-grow -ml-36 text-center'>
                    <h1 className='text-4xl font-bold'>
                        Physical Inspection for{' '}
                        <a
                            className='text-blue-900 underline hover:text-blue-400'
                            href={currentReleaseLine.invLineLink}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {currentReleaseLine.invLineName}
                        </a>
                    </h1>
                </div>
            </header>
            {/* Quantity Mismatch Confirmation Modal */}
            <Modal
                open={quantityMismatchModal}
                setOpen={setQuantityMismatchModal}
                onConfirm={() => {
                    submitInspectionCheckboxReview(physicalInspectionStatuses.INSPECTION_REJECTED);
                }}
                title={'Quantity Mismatch: Do you wish to proceed?'}
                body={
                    <>
                        <p className='text-gray-700'>This will submit a Review Request.</p>
                        <p className='mb-1 text-gray-700'>Quantity entered: {verifyQuantity}</p>
                        <p className='text-gray-700'>RLSL Quantity: {currentReleaseLine.quantity}</p>
                    </>
                }
                confirmText={'Submit Review Request'}
                confirmButtonStyling={
                    'inline-flex w-full bg-red-500 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:col-start-2'
                }
            />
            {/* Expiration Rejection Follow Up Modal */}
            <Modal
                open={expirationRejectionModal}
                setOpen={setExpirationRejectionModal}
                onConfirm={() => submitInspectionCheckboxReview(submissionStatus.type)}
                onCancel={() => setCustomExpirationDate('')}
                title={'You are submitting this line for review due to an invalid expiration date.'}
                body={
                    <>
                        <p className='mb-2 text-gray-600'>
                            Please add the Cure or Expiry Date below, or click "No Shelf Life" to submit without a date.
                        </p>
                        <input
                            type='date'
                            value={customExpirationDate}
                            onChange={(e) => setCustomExpirationDate(e.target.value)}
                            className='px-2 py-1 w-36 text-sm placeholder-gray-500 text-center rounded-md border border-gray-500'
                        />
                    </>
                }
                confirmText={'Confirm'}
                confirmButtonStyling={
                    'inline-flex w-full justify-center rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500 sm:col-start-2'
                }
                extraButtonText={'No Shelf Life'}
                extraButtonFunction={() => {
                    setCustomExpirationDate(physicalInspectionCriteria.NO_SHELF_LIFE);
                    submitInspectionCheckboxReview(submissionStatus.type, physicalInspectionCriteria.NO_SHELF_LIFE, true);
                }}
            />
            {/* Part Number Rejection Follow Up Modal */}
            <Modal
                open={partNumberRejectionModal}
                setOpen={setPartNumberRejectionModal}
                onConfirm={() => submitInspectionCheckboxReview(submissionStatus.type)}
                onCancel={() => setCustomPartNumber('')}
                title={'You are submitting this line for review due to an incorrect part number.'}
                body={
                    <>
                        <p className='mb-2 text-gray-600'>Please enter the correct Part Number below.</p>
                        <input
                            type='text'
                            value={customPartNumber}
                            onChange={(e) => setCustomPartNumber(e.target.value)}
                            placeholder='Actual Part Number'
                            className='px-2 py-1 w-48 text-sm placeholder-gray-500 text-center rounded-md border border-gray-500'
                        />
                    </>
                }
                confirmText={'Confirm'}
                confirmButtonStyling={
                    'inline-flex w-full justify-center rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500 sm:col-start-2'
                }
            />
            {/* Serial Number Rejection Follow Up Modal */}
            <Modal
                open={serialNumberRejectionModal}
                setOpen={setSerialNumberRejectionModal}
                onConfirm={() => submitInspectionCheckboxReview(submissionStatus.type)}
                onCancel={() => setCustomSerialNumber('')}
                title={'You are submitting this line for review due to an incorrect serial number.'}
                body={
                    <>
                        <p className='mb-2 text-gray-600'>Please enter the correct Serial Number below.</p>
                        <input
                            type='text'
                            value={customSerialNumber}
                            onChange={(e) => setCustomSerialNumber(e.target.value)}
                            placeholder='Actual Serial Number'
                            className='px-2 py-1 w-48 text-sm placeholder-gray-500 text-center rounded-md border border-gray-500'
                        />
                    </>
                }
                confirmText={'Confirm'}
                confirmButtonStyling={
                    'inline-flex w-full justify-center rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500 sm:col-start-2'
                }
            />
            <section className='p-2 mt-4 w-full max-w-8xl'>
                {/* //? This will be needed once we want to start displaying the times on the page (not on initial release)
                <div>
                    <p className='text-lg'>
                        Total Timer Per Part:
                        <span className={`font-bold ${partInspectionSeconds > 300 ? 'text-red-600' : 'text-green-600'}`}>
                            {partInspectionTimeDisplay}
                        </span>
                    </p>
                    <p>
                        Department Time/Part: <span className='font-bold'>{'-DEPARTMENT_TIME_AVERAGE-'}</span>
                    </p>
                </div> */}

                <div className='flex relative flex-col items-center bg-white rounded-lg shadow-md'>
                    {!showCamera ? (
                        <>
                            <div className='p-2 w-3/4 text-lg'>
                                <div className='flex justify-between p-2 font-bold'>
                                    <h3>Inspection Requirement</h3>
                                    <h3>Pass Criteria</h3>
                                </div>
                                {inspectionRequirements.map((data) => {
                                    const { requirement, value, pass, id } = data;

                                    return (
                                        <div className='flex p-2 border-b'>
                                            <h3 className='w-3/4' dangerouslySetInnerHTML={{ __html: requirement }}></h3>
                                            <article className='flex flex-row gap-x-3 justify-end items-center w-1/4'>
                                                {id === physicalInspectionCriteria.QUANTITY ? (
                                                    <input
                                                        type='text'
                                                        inputMode='numeric'
                                                        pattern='[0-9]*'
                                                        value={verifyQuantity ? Math.abs(Number(verifyQuantity)) : ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                                                            setVerifyQuantity(val);
                                                        }}
                                                        onBlur={() => {
                                                            const trimmedValue =
                                                                verifyQuantity.trim().replace(/^0+/, '') || '0';
                                                            setVerifyQuantity(trimmedValue);
                                                        }}
                                                        className='px-2 py-0.5 w-20 h-8 text-sm leading-tight placeholder-gray-500 text-center rounded-md border border-gray-500'
                                                    />
                                                ) : (
                                                    <>
                                                        <p>{value}</p>
                                                        <input
                                                            type='checkbox'
                                                            checked={pass}
                                                            className='w-8 scale-125'
                                                            onChange={(e) => {
                                                                const updatedRequirments = inspectionRequirements.map(
                                                                    (req) =>
                                                                        req.id === id
                                                                            ? { ...req, pass: e.target.checked }
                                                                            : req
                                                                );
                                                                setInspectionRequirements(updatedRequirments);
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </article>
                                        </div>
                                    );
                                })}
                            </div>
                            <button
                                disabled={inspectionRequirements.some(
                                    (req) =>
                                        !req.pass &&
                                        !(
                                            // Repair order releases can be partially submitted with mismatched part numbers
                                            (
                                                currentReleaseLine.isRepairOrderRelease &&
                                                inspectionRequirementsAllowedForPartialPass.includes(req.id)
                                            )
                                        )
                                )}
                                className='m-2 bg-green-600 rounded-lg p-2 text-white text-center w-[97%] font-medium disabled:cursor-not-allowed disabled:opacity-50'
                                onClick={() => {
                                    const isRepairOrderPartialPass =
                                        currentReleaseLine.isRepairOrderRelease &&
                                        inspectionRequirements.some(
                                            (req) =>
                                                !req.pass && inspectionRequirementsAllowedForPartialPass.includes(req.id)
                                        );

                                    // Send through a partial pass for the cases where we need to send over a chatter message
                                    if (isRepairOrderPartialPass) {
                                        submitInspectionCheckboxReview(
                                            physicalInspectionStatuses.INSPECTION_PARTIAL_PASSED,
                                            null,
                                            null
                                        );
                                    } else {
                                        submitInspectionCheckboxReview(
                                            physicalInspectionStatuses.INSPECTION_PASSED,
                                            null,
                                            null
                                        );
                                    }
                                }}
                            >
                                {buttonLoading === buttonLoadingTitles.INSPECTION_PASSED ? (
                                    <div className='flex justify-center items-center'>
                                        <LoadingSpinner className='w-44' spinnerSize={'8'} color={'text-white'} />
                                    </div>
                                ) : (
                                    'This Part has met all inspection requirements'
                                )}
                            </button>
                        </>
                    ) : (
                        <div className='p-3 mx-2 mt-2 w-full'>
                            <PhysicalCamera
                                cameras={cameras}
                                currentCameraIndex={currentCameraIndex}
                                loading={loading}
                                photos={photos}
                                setCameras={setCameras}
                                setCurrentCameraIndex={setCurrentCameraIndex}
                                setLoading={setLoading}
                                setPhotos={setPhotos}
                                setVideoStream={setVideoStream}
                                submitApproval={submitPhotoApproval}
                                videoStream={videoStream}
                                buttonLoading={buttonLoading}
                            />
                        </div>
                    )}
                </div>
                {!showCamera ? (
                    <div className='flex flex-col p-3 mt-10 mb-4 bg-gray-100 rounded-lg shadow-lg'>
                        <h2 className='mt-4 text-3xl font-bold'>Request Review</h2>
                        <button
                            disabled={
                                inspectionRequirements.every((requirement) => requirement.pass) ||
                                buttonLoading === buttonLoadingTitles.INSPECTION_REJECTED
                            }
                            className='self-center p-2 mt-3 w-full font-medium text-center text-white bg-red-600 rounded-lg cursor-pointer'
                            onClick={() =>
                                submitInspectionCheckboxReview(physicalInspectionStatuses.INSPECTION_REJECTED, null, null)
                            }
                        >
                            {buttonLoading === buttonLoadingTitles.INSPECTION_REJECTED ? (
                                <div className='flex justify-center items-center'>
                                    <LoadingSpinner className='w-44' spinnerSize={'8'} color={'text-white'} />
                                </div>
                            ) : (
                                'Submit Request'
                            )}
                        </button>
                    </div>
                ) : null}
            </section>
            {/* Submission overlay/modal */}
            {submissionStatus.isSubmitting ? (
                <div className='flex fixed inset-0 z-10 justify-center items-center bg-black bg-opacity-50'>
                    <div className='flex justify-center w-96 h-96 bg-white rounded-lg'>
                        <div className='justify-center items-center w-9/12 backdrop:flex'>
                            <h2 className='my-10 text-2xl font-bold'>Submission Status</h2>
                            {photos.added.length || submissionStatus?.photos === stepConfirmationStatuses.SUCCESS ? (
                                <SubmissionStep text='Uploading Photos' status={submissionStatus?.photos} />
                            ) : null}
                            {findAddedHeroPhotos(photos.current).length ? (
                                <SubmissionStep text='Updating Hero Images' status={submissionStatus.heroImages} />
                            ) : null}
                            {submissionStatus.type === physicalInspectionStatuses.INSPECTION_REJECTED ||
                            submissionStatus.type === physicalInspectionStatuses.INSPECTION_PARTIAL_PASSED ? (
                                <SubmissionStep text='Adding Chatter Message' status={submissionStatus.chatter} />
                            ) : null}
                            {submissionStatus.type === physicalInspectionStatuses.INSPECTION_PARTIAL_PASSED ? (
                                <SubmissionStep text='Saving Inspection Requirements' status={submissionStatus.checkboxes} />
                            ) : (
                                <SubmissionStep
                                    text='Updating Release Line Status'
                                    status={submissionStatus.inspectionStatus}
                                />
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function SubmissionStep({ text, status }) {
    return (
        <div className='flex justify-between items-center my-3 border-b'>
            <p>{text}</p>
            {status !== 'fail' ? (
                <CheckCircleIcon
                    className={
                        'hover:cursor-pointer w-8 h-8 rounded-full duration-100 ' +
                        (status === stepConfirmationStatuses.SUCCESS
                            ? 'bg-green-500 text-white hover:bg-green-400'
                            : status === stepConfirmationStatuses.FAIL
                            ? 'bg-red-500 text-white hover:bg-red-400'
                            : 'bg-white hover:bg-gray-200 border-2 border-gray-500 text-white hover:text-gray-200')
                    }
                />
            ) : (
                <XMarkIcon className='w-8 h-8 text-red-600' />
            )}
        </div>
    );
}
